import * as THREE from 'three';
import type { StyleConfig } from './StyleConfig';
import type { ResolvedStyle } from './OverrideResolver';
import { applyAerialPerspectiveStyle } from './AerialPerspective';
import { applyValueCompositionStyle } from './ValueComposition';
import type { AppRenderer } from './Renderer';

export type SceneLightingStyle = StyleConfig | ResolvedStyle;
const SUN_RIG_DISTANCE = 140;
type DirectionTarget = { x: number; y: number; z: number };

// Reused by `rimLightDirection` every frame in cameraAnchored mode. Hoisted
// so the per-frame call doesn't allocate (and churn the GC) on the hot path.
const RIM_DIR_TMP = new THREE.Vector3();

export function applyRendererStyle(renderer: AppRenderer, style: SceneLightingStyle): void {
  renderer.toneMappingExposure = style.sfx.colorGrade.exposure;
  if (style.sfx.colorGrade.toneMapping === 'None') renderer.toneMapping = THREE.NoToneMapping;
  else if (style.sfx.colorGrade.toneMapping === 'AgX') renderer.toneMapping = THREE.AgXToneMapping;
  else if (style.sfx.colorGrade.toneMapping === 'ACES') renderer.toneMapping = THREE.ACESFilmicToneMapping;
  else renderer.toneMapping = THREE.LinearToneMapping;
}

export function applySkySceneStyle(
  renderer: AppRenderer,
  scene: THREE.Scene,
  fog: THREE.FogExp2 | null,
  horizonColor: THREE.Color,
  style: SceneLightingStyle,
): void {
  horizonColor.set(style.sky.horizonHex);
  applyAerialPerspectiveStyle(style);
  applyValueCompositionStyle(style);
  if (fog) {
    if (style.fog.followSky) fog.color.set(style.sky.horizonHex);
    else fog.color.set(style.fog.colorHex);
    fog.density = style.fog.density;
    scene.fog = fog;
  }
  renderer.setClearColor(horizonColor, 1);
}

export function rimLightDirection(
  style: SceneLightingStyle,
  camera: THREE.Camera,
): DirectionTarget {
  return rimLightDirectionToTarget(style, camera, { x: 0, y: 0, z: 0 });
}

export function rimLightDirectionToTarget(
  style: SceneLightingStyle,
  camera: THREE.Camera,
  target: DirectionTarget,
): DirectionTarget {
  if (style.sunRig.rim.cameraAnchored) {
    const camSpaceDir = style.sunRig.rim.cameraSpaceDir;
    const worldDir = RIM_DIR_TMP
      .set(camSpaceDir.x, camSpaceDir.y, camSpaceDir.z)
      .normalize()
      .applyQuaternion(camera.quaternion);
    target.x = worldDir.x * SUN_RIG_DISTANCE;
    target.y = worldDir.y * SUN_RIG_DISTANCE;
    target.z = worldDir.z * SUN_RIG_DISTANCE;
    return target;
  }
  return directionFromAnglesToTarget(style.sunRig.rim.elevationDeg, style.sunRig.rim.azimuthDeg, SUN_RIG_DISTANCE, target);
}

export function applyLiveSkyStyle(sky: unknown, style: SceneLightingStyle): void {
  const candidate = sky as { applyStyle?: unknown };
  if (typeof candidate.applyStyle === 'function') {
    candidate.applyStyle({
      ...style.sky,
      sunColorHex: style.sunRig.key.colorHex,
      secondarySunColorHex: style.sunRig.fill.colorHex,
      horizonHueShift: (style as Partial<ResolvedStyle>).derivedLighting?.horizonHueShift ?? 0,
    });
  }
}

export function applySunRigStyle(
  key: THREE.DirectionalLight,
  fill: THREE.DirectionalLight,
  hemisphere: THREE.HemisphereLight,
  bounce: THREE.DirectionalLight,
  rim: THREE.DirectionalLight,
  style: SceneLightingStyle,
): void {
  key.color.set(style.sunRig.key.colorHex);
  key.intensity = style.sunRig.key.intensity;
  key.castShadow = style.sunRig.key.castShadow;
  fill.color.set(style.sunRig.fill.colorHex);
  fill.intensity = style.sunRig.fill.intensity;
  hemisphere.color.set(style.sunRig.hemisphere.skyColorHex);
  hemisphere.groundColor.set(style.sunRig.hemisphere.groundColorHex);
  hemisphere.intensity = style.sunRig.hemisphere.intensity;
  bounce.color.set(style.sunRig.bounce.colorHex);
  bounce.intensity = style.sunRig.bounce.intensity;
  rim.color.set(style.sunRig.rim.colorHex);
  rim.intensity = style.sunRig.rim.intensity;
}

export function applyEnvironmentStyle(scene: THREE.Scene, style: SceneLightingStyle): void {
  // This touches material.needsUpdate across the scene, which can trigger
  // shader recompiles. Keep it on boot/style-change paths, never per-frame.
  const intensity = style.sunRig.environmentIntensity;
  (scene as THREE.Scene & { environmentIntensity?: number }).environmentIntensity = intensity;
  scene.traverse((object) => {
    const candidate = object as THREE.Object3D & { material?: THREE.Material | THREE.Material[] };
    const materials = Array.isArray(candidate.material)
      ? candidate.material
      : candidate.material
        ? [candidate.material]
        : [];
    for (const material of materials) {
      if ('envMapIntensity' in material) {
        (material as THREE.Material & { envMapIntensity: number }).envMapIntensity = intensity;
        material.needsUpdate = true;
      }
    }
  });
}

export function keyLightDirection(style: SceneLightingStyle): DirectionTarget {
  return keyLightDirectionToTarget(style, { x: 0, y: 0, z: 0 });
}

export function keyLightDirectionToTarget(style: SceneLightingStyle, target: DirectionTarget): DirectionTarget {
  return adjustedSunDirectionToTarget(style.sky.sunDir, {
    minElevationDeg: style.sunRig.key.minElevationDeg,
    elevationOffsetDeg: style.sunRig.key.elevationOffsetDeg,
    azimuthOffsetDeg: style.sunRig.key.azimuthOffsetDeg,
  }, target);
}

export function fillLightDirection(style: SceneLightingStyle): DirectionTarget {
  return fillLightDirectionToTarget(style, { x: 0, y: 0, z: 0 });
}

export function fillLightDirectionToTarget(style: SceneLightingStyle, target: DirectionTarget): DirectionTarget {
  return adjustedSunDirectionToTarget(style.sky.secondarySunDir, {
    minElevationDeg: style.sunRig.fill.minElevationDeg,
    elevationOffsetDeg: 0,
    azimuthOffsetDeg: 0,
  }, target);
}

export function adjustedSunDirection(
  source: { x: number; y: number; z: number },
  opts: { minElevationDeg: number; elevationOffsetDeg: number; azimuthOffsetDeg: number },
): DirectionTarget {
  return adjustedSunDirectionToTarget(source, opts, { x: 0, y: 0, z: 0 });
}

export function adjustedSunDirectionToTarget(
  source: { x: number; y: number; z: number },
  opts: { minElevationDeg: number; elevationOffsetDeg: number; azimuthOffsetDeg: number },
  target: DirectionTarget,
): DirectionTarget {
  const horizontal = Math.hypot(source.x, source.z);
  const distance = Math.max(SUN_RIG_DISTANCE, Math.hypot(horizontal, source.y));
  const elevation = Math.max(
    opts.minElevationDeg,
    THREE.MathUtils.radToDeg(Math.atan2(source.y, horizontal)) + opts.elevationOffsetDeg,
  );
  const azimuth = THREE.MathUtils.radToDeg(Math.atan2(source.x, source.z)) + opts.azimuthOffsetDeg;
  return directionFromAnglesToTarget(elevation, azimuth, distance, target);
}

export function directionFromAngles(elevationDeg: number, azimuthDeg: number, distance: number): DirectionTarget {
  return directionFromAnglesToTarget(elevationDeg, azimuthDeg, distance, { x: 0, y: 0, z: 0 });
}

export function directionFromAnglesToTarget(
  elevationDeg: number,
  azimuthDeg: number,
  distance: number,
  target: DirectionTarget,
): DirectionTarget {
  const elevation = THREE.MathUtils.degToRad(elevationDeg);
  const azimuth = THREE.MathUtils.degToRad(azimuthDeg);
  const horizontal = Math.cos(elevation) * distance;
  target.x = Math.sin(azimuth) * horizontal;
  target.y = Math.sin(elevation) * distance;
  target.z = Math.cos(azimuth) * horizontal;
  return target;
}

export function updateSunRig(
  sun: THREE.DirectionalLight,
  target: THREE.Object3D,
  focus: THREE.Vector3,
  direction: { x: number; y: number; z: number },
  forwardBias = 0,
): void {
  let fx = focus.x;
  let fy = focus.y;
  let fz = focus.z;
  if (forwardBias !== 0) {
    // Shift focus toward the sun along the light direction. The shadow camera
    // is centered on the target, so biasing forward extends the orthographic
    // frustum upwind to capture distant casters whose long shadows fall on
    // the receiver area at low sun angles.
    const len = Math.hypot(direction.x, direction.y, direction.z) || 1;
    const k = forwardBias / len;
    fx += direction.x * k;
    fy += direction.y * k;
    fz += direction.z * k;
  }
  target.position.set(fx, fy, fz);
  sun.position.set(fx + direction.x, fy + direction.y, fz + direction.z);
}

export interface SunShadowOptions {
  radius?: number;
  size?: number;
  near?: number;
  far?: number;
  mapSize?: number;
}

/**
 * Apply shadow-camera + bias settings to a directional light. Shared between
 * gameplay and the map / rig debug views so a single tuning pipeline drives
 * shadow quality everywhere. Texel density under the receiver is roughly
 * `2 * size / mapSize` meters per shadow texel — tighter `size` or larger
 * `mapSize` makes shadows sharper.
 */
export function configureSunShadow(
  light: THREE.DirectionalLight,
  bias: number,
  normalBias: number,
  options: SunShadowOptions = {},
): void {
  light.castShadow = true;
  const mapSize = options.mapSize ?? 2048;
  light.shadow.mapSize.set(mapSize, mapSize);
  light.shadow.camera.near = options.near ?? 20;
  light.shadow.camera.far = options.far ?? 220;
  const size = options.size ?? 70;
  light.shadow.camera.left = -size;
  light.shadow.camera.right = size;
  light.shadow.camera.top = size;
  light.shadow.camera.bottom = -size;
  light.shadow.bias = bias;
  light.shadow.normalBias = normalBias;
  light.shadow.radius = options.radius ?? 2;
  light.shadow.camera.updateProjectionMatrix();
}
